// exam-page.js: browser entry for the mock-exam page. Bundled by esbuild → dist/assets/b/exam-<hash>.js.
// Builds one exam from a seed, renders every question, and on submit scores it with the real
// PPL rules and reveals the answers. A fresh exam de-prioritizes lessons drawn in the last few
// exams (localStorage FIFO, PLAN §5.2); `?seed=` replays a seed exactly, ignoring that history.
// A fresh exam's result is saved for the home page (progress.js); a replay saves nothing.
// Only DOM code lives here; the assembly + scoring is the pure engine in exam.js.

import { buildExam, scoreExam, PASS_PCT } from './exam.js';
import { BLUEPRINT } from './blueprint.js';
import { load, remember, save } from './store.js';
import { STORE, examRecord } from './progress.js';
import { SECTION_LABEL } from './sections.js';
import { el } from './el.js';

const $ = (id) => document.getElementById(id);

const HISTORY = 'exam-topics';
const HISTORY_EXAMS = 3; // remember this many exams' worth of lessons
const EXAM_SIZE = Object.values(BLUEPRINT).reduce((n, b) => n + b.total, 0);

const replay = Number(new URLSearchParams(location.search).get('seed')) >>> 0;
const seed = replay || Date.now() >>> 0;
const { questions } = buildExam(seed, replay ? new Set() : new Set(load(HISTORY)));
if (!replay) save(HISTORY, remember(load(HISTORY), questions.map((q) => q.topic), HISTORY_EXAMS * EXAM_SIZE));

function render() {
  if (replay) $('seed').textContent = `seed ${seed} (replay)`;
  $('questions').replaceChildren(
    ...questions.flatMap((q, i) => {
      const head = q.section === questions[i - 1]?.section ? [] : [el('h2', { textContent: SECTION_LABEL[q.section] ?? q.section })];
      const opts = q.options.map((o, j) =>
        el('label', { className: 'opt' }, el('input', { type: 'radio', name: `q${i}`, value: j }), ` ${o.text}`),
      );
      return [...head, el('fieldset', { className: 'q' }, el('legend', { className: 'stem', textContent: `${i + 1}. ${q.stem}` }), ...opts)];
    }),
  );
}

const answers = () =>
  questions.map((_, i) => {
    const checked = document.querySelector(`input[name="q${i}"]:checked`);
    return checked ? Number(checked.value) : null;
  });

function grade() {
  const chosen = answers();
  const result = scoreExam(questions, chosen);
  if (!replay) save(STORE.exam, examRecord(result, Date.now()));

  // Reveal correct/wrong on every option and lock the form.
  document.querySelectorAll('fieldset.q').forEach((fs, i) => {
    const q = questions[i];
    [...fs.querySelectorAll('label.opt')].forEach((label, j) => {
      label.querySelector('input').disabled = true;
      if (q.options[j].correct) label.classList.add('correct');
      else if (j === chosen[i]) label.classList.add('wrong');
    });
  });

  const rows = Object.entries(result.sections)
    .map(
      ([s, v]) =>
        `<li>${SECTION_LABEL[s] ?? s}: ${v.correct}/${v.total} (${v.pct.toFixed(0)}%) ${v.pass ? '✓' : '✗'}</li>`,
    )
    .join('');
  const r = $('results');
  r.innerHTML =
    `<h2>${result.pass ? 'PASS' : 'FAIL'}: ${result.correct}/${result.total} (${result.overallPct.toFixed(0)}%)</h2>` +
    `<p class="tag">Pass needs ${PASS_PCT}% overall and ${PASS_PCT}% in every section.</p><ul>${rows}</ul>`;
  r.classList.remove('hidden');
  r.scrollIntoView({ behavior: 'smooth' });
  $('submit').disabled = true;
}

render();
$('submit').onclick = grade;
